"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FulfillmentShipmentClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const errors_1 = require("@deliverr/errors");
const ReplacementData_1 = require("../orders/ReplacementData");
const FulfillmentShipmentExceptions_1 = require("./FulfillmentShipmentExceptions");
const commons_logs_1 = require("@deliverr/commons-logs");
const FULFILLMENT_SHIPMENT_BASE_URL = process.env.FULFILLMENT_SHIPMENT_BASE_URL;
const FULFILLMENT_SHIPMENT_BASIC_AUTH_USER = process.env.FULFILLMENT_SHIPMENT_BASIC_AUTH_USER;
const FULFILLMENT_SHIPMENT_BASIC_AUTH_PASSWORD = process.env.FULFILLMENT_SHIPMENT_BASIC_AUTH_PASSWORD;
class FulfillmentShipmentClient extends base_client_1.ApiBasicAuthClient {
    constructor(config = {
        baseURL: FULFILLMENT_SHIPMENT_BASE_URL,
        basicAuthUser: FULFILLMENT_SHIPMENT_BASIC_AUTH_USER,
        basicAuthPassword: FULFILLMENT_SHIPMENT_BASIC_AUTH_PASSWORD,
    }) {
        super(config);
        if (!config.baseURL) {
            commons_logs_1.log.warn("FULFILLMENT_SHIPMENT_BASE_URL environment variable is not set");
        }
        if (!config.basicAuthUser || !config.basicAuthPassword) {
            commons_logs_1.log.warn("FULFILLMENT_SHIPMENT_BASIC_AUTH_USER and/or FULFILLMENT_SHIPMENT_BASIC_AUTH_PASSWORD environment variable is not set");
        }
    }
    async getShipmentById(shipmentId) {
        try {
            const shipment = await this.get(`/v1/shipment/${shipmentId}`);
            if (shipment) {
                return Object.assign(Object.assign({}, shipment), { id: shipment.id.toString(), orderId: shipment.orderId.toString() });
            }
        }
        catch (error) {
            if (error.response.status === 404) {
                return undefined;
            }
            throw (0, base_client_1.mapError)(error);
        }
    }
    async getShipmentsByOrderId(orderId) {
        try {
            return await this.get(`/v1/shipments/${orderId}`);
        }
        catch (error) {
            if (error.response.status === 404) {
                return [];
            }
            throw (0, base_client_1.mapError)(error);
        }
    }
    async getShipments(shipmentIds) {
        try {
            return await this.post(`/v1/shipments`, { shipmentIds });
        }
        catch (error) {
            if (error.response.status === 404) {
                return [];
            }
            throw (0, base_client_1.mapError)(error);
        }
    }
    async fetchAndUpdateDeliveryDisruptions(updateDeliveryDisruptionsRequest) {
        try {
            return await this.put(`/v1/shipment/fetch-update-delivery-disruptions`, updateDeliveryDisruptionsRequest);
        }
        catch (error) {
            if (error.response.status === 404) {
                return undefined;
            }
            throw (0, base_client_1.mapError)(error);
        }
    }
    async addWarningToShipmentByShipmentId(shipmentId, warning) {
        try {
            return await this.put(`/v1/shipment/${shipmentId}/warning`, { warning });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async addLabelPrintTimeToShipmentByShipmentId(shipmentId) {
        try {
            return await this.put(`/v1/shipment/${shipmentId}/labelPrintTime`, {});
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async cancelShipment(shipmentId, params = { forceCancel: false }) {
        try {
            const path = `/v1/shipment/${shipmentId}`;
            return await this.delete(path, params);
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async registerCapacityIfNecessary(shipmentId, updatedReleaseTime) {
        try {
            return await this.put(`/v1/shipment/registerCapacityIfNecessary`, {
                shipmentId,
                updatedReleaseTime,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async generateShipments(orderId) {
        try {
            return await this.put(`/v1/shipments/generate/${orderId}`, {});
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async updateShipmentStatus(shipmentStatus) {
        const { deliverrShipmentId } = shipmentStatus;
        try {
            return await this.put(`/v1/shipment-update/${deliverrShipmentId}`, shipmentStatus);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(FulfillmentShipmentExceptions_1.FulfillmentShipmentExceptions.INVALID_SHIPMENT_STATE);
                case 404:
                    throw new errors_1.DeliverrError(FulfillmentShipmentExceptions_1.FulfillmentShipmentExceptions.NOT_FOUND);
                default:
                    throw new errors_1.DeliverrError(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async createReplacementShipmentAndReimbursement(shipmentIds, createdBy) {
        return this.post("/v1/ops/shipments/replace", {
            shipmentIds,
            createdBy,
        });
    }
    async createReplacementShipment(lostShipmentId, createdBy) {
        try {
            return await this.post("/v1/ops/workflow/replacement", {
                lostShipmentId,
                createdBy,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async findLostShipmentByReplacementId(replacementShipmentId) {
        var _a;
        try {
            return await this.get(`/v1/ops/workflow/lost-shipment/${replacementShipmentId}`);
        }
        catch (error) {
            switch ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) {
                case 404:
                    return undefined;
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.UNKNOWN_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async getShipmentReplacements(shipmentReplacementRequestInfo) {
        var _a;
        try {
            return await this.get(`/v1/ops/workflow/replacement`, shipmentReplacementRequestInfo);
        }
        catch (error) {
            switch ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) {
                case 400:
                    throw new errors_1.DeliverrError({
                        code: ReplacementData_1.ShipmentReplacementException.NO_FIELDS_PROVIDED,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.UNKNOWN_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async cancelShipments(shipmentIds, params) {
        const results = await Promise.allSettled(shipmentIds.map((shipmentId) => this.cancelShipment(shipmentId, params)));
        const failures = [];
        results.forEach((result, idx) => {
            if (result.status !== "fulfilled") {
                failures.push({
                    id: shipmentIds[idx],
                    reason: result.reason,
                });
            }
        });
        return failures;
    }
    async processWarehouseUpdates(shipmentId) {
        try {
            await this.put(`/v1/shipment-update/${shipmentId}/sync-with-warehouse`, undefined);
            return { isProcessed: true };
        }
        catch (error) {
            if (error.response && error.response.status === 500) {
                return { isProcessed: false };
            }
            throw (0, base_client_1.mapError)(error);
        }
    }
    async updateShipmentSortCode(shipmentId, sortCode) {
        try {
            return await this.put(`/v1/workflow/shipment/${shipmentId}/sort-code`, { sortCode });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
}
exports.FulfillmentShipmentClient = FulfillmentShipmentClient;
//# sourceMappingURL=FulfillmentShipmentClient.js.map